import { AntDesign } from "@expo/vector-icons";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import {
  withObservables,
  ExtractedObservables,
} from "@nozbe/watermelondb/react";
import {
  Badge,
  Card,
  ProductThumbnail,
  Stack,
  Text,
} from "@towersystems/roam-common-native";
import {
  moneyToFormattedString,
  toTitleCase,
} from "@towersystems/roam-common/lib/shared-utils";
import React from "react";
import { Colors, Spacings, TouchableOpacity, View } from "react-native-ui-lib";
import Product from "../../../../utilities/data/model/Product";

export interface ProductSummaryCardProps {
  productId: string;
  locationId: string;
  onPress?: (product: Product) => void;
}

const getObservables = ({ product }: { product: Product }) => ({
  product: product.observe(),
  productKit: product.productKit?.observe(),
});

export interface ProductSummaryProps
  extends ExtractedObservables<ReturnType<typeof getObservables>> {
  locationId: ProductSummaryCardProps["locationId"];
  onPress?: ProductSummaryCardProps["onPress"];
}

const ProductSummaryRenderer = ({
  product,
  productKit,
  locationId,
  onPress,
}: ProductSummaryProps) => {
  const inventoryPricing = React.useMemo(() => {
    const inventoryPricing = product
      ? product.locationInventoryPricing.find(
          (pricing) => pricing.locationId === locationId
        )
      : undefined;

    if (inventoryPricing) {
      return inventoryPricing;
    }

    return (
      product.locationInventoryPricing.find((p) => p.locationId === "0") ??
      product.locationInventoryPricing?.[0]
    );
  }, []);

  const isRetailerProduct = product.stockCode && product.stockCode !== "0";

  const badges = [];
  if (!product.internal) {
    badges.push(
      <Badge
        key="productSource"
        productSource={
          isRetailerProduct ? "RETAILER-PRODUCT" : "NON-RETAILER-PRODUCT"
        }
      />
    );
  }

  if (product?.variantParentId) {
    if (product.variantParentId == product.id) {
      badges.push(<Badge key="MAIN_PRODUCT" productType="MAIN_PRODUCT" />);
    } else {
      badges.push(
        <Badge key="VARIANT_PRODUCT" productType="VARIANT_PRODUCT" />
      );
    }
  }

  if (!!product.productKit?.id) {
    badges.push(<Badge key="KIT_PRODUCT" productType="KIT_PRODUCT" />);
  }

  const content = (
    <Card filled={false} flex>
      <View
        flex
        row
        style={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <View
          style={{ alignItems: "center", flexBasis: "100%", flexShrink: 1 }}
          row
        >
          <ProductThumbnail
            imageUrl={product.imageUrl}
            hideQuantity
            isInternalProduct={false}
            size="large"
          />
          <View marginL-20 style={{ flexShrink: 1 }}>
            <Text variant="heading1" numberOfLines={1} ellipsizeMode="head">
              {toTitleCase(product.name || "")}
            </Text>
            <Stack spacing={Spacings.s1}>{badges}</Stack>
            <Text>{product.displayName}</Text>
            <Text color="subdued">
              <Text fontWeight="bold">Stock Number: </Text>
              {product.stockNumber || product.stockNumber || ""}
            </Text>
            <Text color="subdued">
              <Text fontWeight="bold">Supplier Code: </Text>
              {product.supplierStockCode ?? "N/A"}
            </Text>
          </View>
        </View>
        <View style={{ marginLeft: Spacings.s4 }} row>
          <View>
            <Text variant="heading1">
              {moneyToFormattedString(
                productKit?.price || inventoryPricing?.retailPrice || 0
              )}
            </Text>
            <Text>RETAIL</Text>
          </View>
          {onPress && (
            <Text>
              <AntDesign
                style={[{ marginLeft: Spacings.s4, color: Colors.Text }]}
                name="right"
                size={24}
              />
            </Text>
          )}
        </View>
      </View>
    </Card>
  );

  return onPress ? (
    <TouchableOpacity onPress={() => onPress(product)}>
      {content}
    </TouchableOpacity>
  ) : (
    content
  );
};

const enhance = withObservables(["product"], getObservables);

const ProductSummary = enhance(ProductSummaryRenderer);

export const ProductSummaryCard = ({
  onPress,
  productId,
  ...props
}: ProductSummaryCardProps) => {
  const database = useDatabase();
  const [product, setProduct] = React.useState<Product | undefined>(undefined);
  const [_, setError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    // loading the product here first so that we can garuntee that the product
    // exists and wont throw an error when we try to load it from the watermelondb
    database
      .get<Product>("products")
      .find(productId)
      .then((product) => {
        setProduct(product);
      })
      .catch((err) => {
        setError(err.message ?? "");
      });
  }, [productId]);

  if (!product) {
    return undefined;
  }

  return (
    <ProductSummary
      {...props}
      product={product}
      onPress={product.internal ? undefined : onPress}
    />
  );
};
